import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import Student from './Student';
import Lessons from './Lessons';
import LessonCalendar from './LessonCalendar';
import StudentDetailPanel from './StudentDetailPanel';
import SettingsPanel from './Settings';

import { 
  Users, 
  BookOpen, 
  Calendar, 
  TrendingUp, 
  ClipboardList, 
  Settings,
  Menu,
  Home,
  X,
  LogOut
} from 'lucide-react';
import HomeworkDashboard from './Homework';

const TutorPortal = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { logout, user } = useAuth();

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const menuItems = [
    { 
      id: 'dashboard', 
      label: 'Dashboard', 
      icon: <Home className="w-5 h-5" />,
      color: 'text-blue-400'
    },
    { 
      id: 'students', 
      label: 'Students', 
      icon: <Users className="w-5 h-5" />,
      color: 'text-green-400'
    },
    { 
      id: 'lessons', 
      label: 'Lessons', 
      icon: <BookOpen className="w-5 h-5" />,
      color: 'text-amber-400'
    },
    { 
      id: 'homework', 
      label: 'Homework', 
      icon: <ClipboardList className="w-5 h-5" />,
      color: 'text-indigo-400'
    },
    { 
      id: 'metrics', 
      label: 'Analytics', 
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'text-purple-400'
    }
  ];

  const settingsItem = {
    id: 'settings',
    label: 'Settings',
    icon: <Settings className="w-5 h-5" />,
    color: 'text-gray-400'
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <LessonCalendar/>
        );
      case 'students':
        return <div className="p-4"><Student/></div>;
      case 'lessons':
        return <div className="p-4"><Lessons/></div>;
      case 'homework':
        return <div className="p-4"><HomeworkDashboard/></div>;
      case 'metrics':
        return <div className="p-4">Analytics and performance metrics coming soon!</div>;
      case 'settings':
        return <div className="p-4"><SettingsPanel/></div>;
      default:
        return <div className="p-4">Select a menu item</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="md:hidden bg-white p-4 shadow-sm flex justify-between items-center sticky top-0 z-10">
        <span className="text-xl font-semibold text-blue-600">Verve Tutor</span>
        <div className="flex items-center space-x-2">
          <button 
            onClick={logout}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
            title="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      <div className="flex flex-col md:flex-row">
        {/* Sidebar - Using DeepSeek blue (#175CE6) */}
        <aside 
          className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          md:translate-x-0 transform transition-transform duration-200 ease-in-out
          fixed md:static inset-y-0 left-0 w-64 bg-[#175CE6] 
          text-white min-h-screen shadow-lg z-20`}
        >
          <div className="p-4 hidden md:block">
            <h2 className="text-2xl font-bold text-white">Verve Tutor</h2>
            <p className="text-blue-100 text-sm">Teaching made effortless</p>
          </div>
          
          <nav className="mt-6 px-2 space-y-1">
            {/* Main Menu Items */}
            <div className="mb-4">
              <div className="w-full flex justify-between items-center px-4 py-2 text-blue-100 rounded-lg">
                <span className="font-medium">Main Menu</span>
              </div>
              
              <div className="mt-2 space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setActiveTab(item.id);
                      if (isMobile) setSidebarOpen(false);
                    }}
                    className={`w-full flex items-center px-4 py-3 text-sm rounded-lg transition-all
                      ${activeTab === item.id ? 
                        'bg-white text-blue-600 font-medium' : 
                        'text-blue-100 hover:bg-blue-500 hover:text-white'}
                    `}
                  >
                    <span className={`${item.color} mr-3`}>{item.icon}</span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Settings Section */}
            <div>
              <button
                onClick={() => {
                  setActiveTab(settingsItem.id);
                  if (isMobile) setSidebarOpen(false);
                }}
                className={`w-full flex items-center px-4 py-3 text-sm rounded-lg transition-all
                  ${activeTab === settingsItem.id ? 
                    'bg-white text-blue-600 font-medium' : 
                    'text-blue-100 hover:bg-blue-500 hover:text-white'}
                `}
              >
                <span className={`${settingsItem.color} mr-3`}>{settingsItem.icon}</span>
                <span>{settingsItem.label}</span>
              </button>
            </div>
          </nav>

          {/* User Profile and Logout */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-blue-500 hidden md:block">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="h-10 w-10 rounded-full bg-blue-400 flex items-center justify-center text-white">
                  {user?.email ? user.email.substring(0, 2).toUpperCase() : 'TU'}
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-white">
                    {user?.email ? user.email.split('@')[0] : 'Tutor User'}
                  </p>
                  <p className="text-xs text-blue-100">Premium Account</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="p-2 rounded-md text-blue-100 hover:bg-blue-500 hover:text-white transition-colors"
                title="Logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8">
          <h1 className="text-2xl font-semibold mb-6 text-gray-800">
            {[...menuItems, settingsItem].find(item => item.id === activeTab)?.label}
          </h1>
          {renderContent()}
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && isMobile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default TutorPortal;